import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import saveAs from 'file-saver';
import { Vessel, VesselsService} from 'src/app/transportation/vessels/vessels.service';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { forkJoin } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PgMessagesService, MessageLevel } from 'src/app/shared/services/pg-messages.service';
import { DocumentReference, DocumentService } from '../../../documents/documents.service';
import { PgTitleComponent } from '../../../shared/components/title-bar/title-bar.component';
import { KeycloakService } from 'src/app/authentication/keycloak.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatInputModule } from '@angular/material/input';
import { MaterialAutoCompleteComponent } from 'src/app/shared/components/material-autocomplete/material-auto-complete.component';
import { MaterialOrganisationSelectorComponent } from 'src/app/shared/components/material-autocomplete/material-organisationselector.component';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MaterialChunkFileUploadComponent } from 'src/app/shared/components/material-file-uploader/material-chunk-file-upload.component';
import { MaterialPdfViewerComponent } from 'src/app/shared/components/material-pdf-viewer/material-pdf-viewer.component';
import { BunkeringDeliveryNote, BunkeringSof, BunkeringSpec } from 'src/app/generated/services/transportation/reports/bunker';
import { MaterialOffsetDateTimeComponent } from 'src/app/shared/components/material-offset-date-time/material-offset-date-time.component';
import { BunkerServiceSpa } from 'src/app/generated/services/transportation/reports/bunkerSpa';
import { MaterialPgButtonComponent } from 'src/app/shared/components/material-pg-button/material-pg-button.component';
import { MaterialPgInputComponent } from 'src/app/shared/components/material-pg-input/material-pg-input.component';
enum BunkerType {
  HFO = 'HFO',
  VLSFO = 'VLSFO',
  ULSFO = 'ULSFO',
  MGO = 'MGO',
  LSMGO = 'LSMGO',
}

@Component({
  selector: 'app-bunker-data-entry',
  templateUrl: './bunker-data-entry.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MatInputModule,
    MaterialAutoCompleteComponent,
    MaterialOrganisationSelectorComponent,
    MatIconModule,
    MatSelectModule,
    MaterialChunkFileUploadComponent,
    MaterialPdfViewerComponent,
    MaterialOffsetDateTimeComponent,
    MaterialPgButtonComponent,
    MaterialPgInputComponent,
  ]
})
export class BunkerDataEntryComponent implements OnInit, PgTitleComponent {
  title: EventEmitter<string> = new EventEmitter();
  toolbarComponent: any;
  vessels: Vessel[] = [];
  vesselImo: string | null = null;
  isoDate: string | null = null;
  bdnNumber: string | null = null;
  ports: Organisation[] = [];
  bunkeringDeliveryNote: BunkeringDeliveryNote = BunkeringDeliveryNote.create();
  selectedPortOrganisation: Organisation | undefined ;
  selectedSupplierOrganisation: Organisation | undefined;
  message: string = '';
  product: string | null = null;
  supplier: Organisation[] = [];
  productName = [
    BunkerType.HFO,
    BunkerType.VLSFO,
    BunkerType.ULSFO,
    BunkerType.MGO,
    BunkerType.LSMGO
  ];
  disable: boolean = false;
  valid: boolean = false;
  selectedVessel: Vessel = Vessel.create();
  expectedVersion: number = 0;
  doc?: DocumentReference;
  doc2?: DocumentReference;

  constructor(private _activateRoute: ActivatedRoute,
    private _organisationService: OrganisationService,
    private _router: Router,
    private _keycloakService: KeycloakService,
    private _pgMessagesService: PgMessagesService,
    private _vesselsService: VesselsService,
    private _documentsService: DocumentService,
    private _bunkerService: BunkerServiceSpa,
  ) {
  }

  ngOnInit() {
    this.valid = false;
    this.title.emit('Bunker Delivery Note');
    this._activateRoute.paramMap.subscribe(p => {
      this.vesselImo = p.get('vesselImo');
      this.isoDate = p.get('date');
      this.product = p.get('product');
      this.bdnNumber = p.get('bdnNumber');
      this.bunkeringDeliveryNote = BunkeringDeliveryNote.create({
        spec: BunkeringSpec.create(),
        sof: BunkeringSof.create(),
      });
      forkJoin([
        this._organisationService.getPorts(),
        this._organisationService.getSupplier(),
        this._vesselsService.getVessels()]
      ).subscribe(results => {
        this.ports = results[0];
        this.supplier = results[1];
        this.vessels = results[2].filter(v => v.active);
        this.initBunkerDeliveryNote();
      });
    });
  }

  hasRole(roleName: string) {
    return this._keycloakService.hasRole(roleName);
  }

  async initBunkerDeliveryNote() {
    if (this.vesselImo && this.isoDate && this.product && this.bdnNumber) {
      this.disable = true;
      const t = await this._bunkerService.getOneBunkerDeliveryNote({
        vesselImo: this.vesselImo,
        product: this.product,
        timestamp: this.isoDate,
        bdnNumber: this.bdnNumber
      });
      this.bunkeringDeliveryNote = t.data ?? BunkeringDeliveryNote.create();
      this.expectedVersion = t.version;
      const foundVessel = this.vessels.find(v => v.imo === this.vesselImo);
      if (foundVessel) {
        this.selectedVessel = foundVessel;
      }
      this.selectedPortOrganisation = this.ports.find(p => p.name === this.bunkeringDeliveryNote?.locationOrganisationId);
      this.selectedSupplierOrganisation = this.supplier.find(p => p.name === this.bunkeringDeliveryNote?.supplierOrganisationId);
      this.valid = this.bunkeringDeliveryNote?.fileId ? true : false;
    }
  }

  uploadBDNFile(bdn: DocumentReference) {
    this.bunkeringDeliveryNote.fileId = bdn.id;
  }

  uploadFuelAnalysis(v: DocumentReference) {
    this.bunkeringDeliveryNote.fuelFileIds.push(v.id);
  }

  async remove() {
    if (confirm('Are you sure you want to delete this BDN?') && this.bunkeringDeliveryNote) {
      this.bunkeringDeliveryNote.isDeleted = true;
      await this._bunkerService.updateBunkerDeliveryNote({
        data:this.bunkeringDeliveryNote,
        expectedVersion: this.expectedVersion
      });
      this._router.navigate(['/vessels/bunkers']);
      this.message = 'Remove BDN';
      this._pgMessagesService.publishMessage({
        message: this.message,
        topic: 'Deleted',
        level: MessageLevel.Info
      });
    }
  }

  async save() {
    this.bunkeringDeliveryNote.vesselImo = this.selectedVessel.imo;
    this.bunkeringDeliveryNote.vesselName = this.selectedVessel!.name!.name;
    this.bunkeringDeliveryNote.supplierOrganisationId = this.selectedSupplierOrganisation!.name;
    this.bunkeringDeliveryNote.locationOrganisationId = this.selectedPortOrganisation!.name;
    if(this.disable) {
      await this._bunkerService.updateBunkerDeliveryNote({
        data: this.bunkeringDeliveryNote,
        expectedVersion: this.expectedVersion});
      this.message = `${this.bunkeringDeliveryNote.vesselName}`;
      this._pgMessagesService.publishMessage({
        message: this.message,
        topic: 'Submitting BDN successfully ',
        level: MessageLevel.Info
      });
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router
          .navigate([
            'vessels/bunker',
            this.bunkeringDeliveryNote.vesselImo,
            this.bunkeringDeliveryNote.product,
            this.bunkeringDeliveryNote.date?.isoDate,
            this.bunkeringDeliveryNote.bdnNumber,
          ])
          .then((e) => console.log(`Result of Navigation is ${e}`));
      });
    } else {
      await this._bunkerService.addBunkerDeliveryNote(this.bunkeringDeliveryNote);
      this.message = `${this.bunkeringDeliveryNote.vesselName}`;
      this._pgMessagesService.publishMessage({
        message: this.message,
        topic: 'Submitting BDN successfully ',
        level: MessageLevel.Info
      });
    }
  }

  downloadFile(fileId: string) {
    this._documentsService.download(fileId).subscribe(b => {
      saveAs(b.body, `${this.selectedSupplierOrganisation?.name}.pdf`);
    }, () => {
      this._pgMessagesService.publishMessage({
        message: 'Could not download this file.',
        level: MessageLevel.Error,
        topic: 'Voyage document'
      });
    });
  }

  vesselFormatter(vessel: Vessel) {
    if (vessel && vessel.name) {
      return vessel.name.name;
    }
    return '';
  }
  productFormatter(product: string) {
    if (product) {
      return product;
    }
    return '';
  }

}